import { c as _c } from "react/compiler-runtime";
import { useContext, type EventHandler, type MouseEvent } from 'react';
import CN from 'classnames';
import { batch, connect } from 'react-redux';
import { Category, isLeafCategory } from '@sbt-web/category-utils';
import { useViewport } from '@sbt-web/hooks';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';
import { CategoryID, type AdTypes, type CompositeFilter, type ListFilter } from '@sbt-web/network/types';
import { Button } from '@sbt-web/ui';
import { wholeNumberDigit } from '@sbt-web/utils';
import type { RootState } from '@/reducers/index';
import { updateFilter, updateFilterLabel } from '@/reducers/search';
import { FiltersStore } from '@/tools/search/getFiltersStore';
import RadiusSelector from '../Elements/Filters/RadiusSelector';
import { GeoZoneSelector } from '../Elements/Filters/ZoneSelector';
import { Filter } from './Filter';
import FiltersSkeletons from './FiltersSkeletons';
import classes from './FiltersContainer.module.scss';
interface OwnProps {
  onApply?: EventHandler<MouseEvent>;
}
interface StateProps {
  category: Category;
  adType: AdTypes;
  itemsTotal: number;
  activeFilters: {
    [key: string]: string;
  };
  filtersStore: FiltersStore | null;
  filtersStoreLoading: boolean;
  filtersWithBadgeNew: string[];
}
interface DispatchProps {
  updateFilterAndLabel: (key: string, value?: string, label?: string, uri?: string) => void;
}
type Props = OwnProps & StateProps & DispatchProps;
const categoriesWithRadiusSearch: CategoryID[] = [CategoryID.Immobili, CategoryID.Appartamenti, CategoryID.CamerePostiLetto, CategoryID.Ville, CategoryID.TerreniRustici, CategoryID.GarageBox, CategoryID.LoftMansarde, CategoryID.CaseVacanza, CategoryID.UfficiLocali];
const isCategoryWithRadiusSearch = (category: Category): boolean => categoriesWithRadiusSearch.includes(category.id);
export const FiltersContainer = t0 => {
  const $ = _c(31);
  const {
    activeFilters,
    adType,
    category,
    filtersStore: currentConfig,
    filtersStoreLoading: configLoading,
    filtersWithBadgeNew,
    itemsTotal,
    onApply,
    updateFilterAndLabel
  } = t0;
  const {
    isMobile
  } = useViewport();
  const {
    optimizely
  } = useContext(OptimizelySubitoContext);
  let t1;
  if ($[0] !== optimizely) {
    t1 = () => {
      optimizely?.onReady().then(() => {
        optimizely?.track("subito_filters_apply");
      });
    };
    $[0] = optimizely;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const trackSearchConversion = t1;
  let t2;
  if ($[2] !== currentConfig?.filters || $[3] !== trackSearchConversion || $[4] !== updateFilterAndLabel) {
    t2 = filter => (key, value, label) => {
      const dependentListFilters = currentConfig?.filters.filter(f => f.type === "list" && (f.dependencies?.includes(filter.uri) ?? false)) ?? [];
      for (const depFilter of dependentListFilters) {
        updateFilterAndLabel(depFilter.queryString, undefined, undefined, filter.uri);
      }
      updateFilterAndLabel(key, value, label, filter.uri);
      trackSearchConversion();
    };
    $[2] = currentConfig?.filters;
    $[3] = trackSearchConversion;
    $[4] = updateFilterAndLabel;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onFilterSelected = t2;
  let t3;
  if ($[6] !== isMobile) {
    t3 = CN(classes.container, {
      [classes["in-dialog"]]: isMobile
    });
    $[6] = isMobile;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== isMobile) {
    t4 = !isMobile && <h5 className={CN("headline-5", classes["filter-title"])}>Filtri ricerca</h5>;
    $[8] = isMobile;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== activeFilters || $[11] !== adType || $[12] !== category || $[13] !== configLoading || $[14] !== currentConfig || $[15] !== filtersWithBadgeNew || $[16] !== isMobile || $[17] !== onFilterSelected || $[18] !== trackSearchConversion) {
    t5 = configLoading ? <FiltersSkeletons size={isMobile ? "large" : "small"} /> : <>{!isMobile && isCategoryWithRadiusSearch(category) && <RadiusSelector trackSearchConversion={trackSearchConversion} />}{isLeafCategory(category) && category.zoneSupport && <GeoZoneSelector trackSearchConversion={trackSearchConversion} />}{currentConfig?.filters.map(filter_0 => <Filter key={`${filter_0.uri}_${category.id}_${adType}`} filter={filter_0} category={category} onFilterSelected={onFilterSelected(filter_0)} activeFilters={activeFilters} trackSearchConversion={trackSearchConversion} filterStore={currentConfig} showBadgeNew={filtersWithBadgeNew.includes(filter_0.uri)} />)}{isMobile && <div className={classes["btn-clearfix"]} />}</>;
    $[10] = activeFilters;
    $[11] = adType;
    $[12] = category;
    $[13] = configLoading;
    $[14] = currentConfig;
    $[15] = filtersWithBadgeNew;
    $[16] = isMobile;
    $[17] = onFilterSelected;
    $[18] = trackSearchConversion;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t4 || $[21] !== t5) {
    t6 = <div className={classes.filters}>{t4}{t5}</div>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== isMobile || $[24] !== itemsTotal || $[25] !== onApply) {
    t7 = isMobile && <div className={classes["btn-container"]}><Button onClick={onApply} classes={[classes["view-results"]]}><span className="button-text">Vedi {wholeNumberDigit.format(itemsTotal)} annunci{itemsTotal === 1 && "o"}</span></Button></div>;
    $[23] = isMobile;
    $[24] = itemsTotal;
    $[25] = onApply;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== t3 || $[28] !== t6 || $[29] !== t7) {
    t8 = <div id="filters-container" className={t3}>{t6}{t7}</div>;
    $[27] = t3;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
};
export default connect<StateProps, DispatchProps, unknown, RootState>(state => ({
  category: state.search.category,
  adType: state.search.adType,
  activeFilters: state.search.filters,
  itemsTotal: state.items.total,
  filtersStore: FiltersStore.fromSerialized(state.filtersConfig.filtersStore),
  filtersStoreLoading: state.filtersConfig.loading,
  filtersWithBadgeNew: state.filtersConfig.filtersWithBadgeNew
}), dispatch => ({
  updateFilterAndLabel: (key: string, value: string = '', label: string = '', uri: string = '') => {
    batch(() => {
      dispatch(updateFilter({
        key,
        value,
        uri
      }));
      dispatch(updateFilterLabel({
        key,
        value: label
      }));
    });
  }
}))(FiltersContainer);